// TFVF Certification — trainee sees their own gates; managers can also
// evaluate any team member and issue the certificate when all 5 gates
// pass. The gate rules are enforced server-side; this UI is a mirror.
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, ShieldCheck, CheckCircle2, XCircle, Clock, Award,
  ChevronRight, BookOpen, ClipboardCheck, Users, Phone,
} from 'lucide-react';
import { API, authHeaders } from './tfvfShared';
import { useAuth } from '../../../contexts/AuthContext';

const GATE_META = {
  modules_complete:  { label: 'All required modules complete', icon: BookOpen },
  knowledge_pass:    { label: 'Knowledge score ≥ 85%',         icon: ClipboardCheck },
  practical_pass:    { label: 'Practical assessment ≥ 70%',    icon: Award },
  acknowledgements:  { label: 'Platform acknowledgements signed', icon: ShieldCheck },
  real_call_demo:    { label: 'Behaviour demonstrated in 2 of next 3 calls', icon: Phone },
};

const GateRow = ({ gate, ok, extra }) => {
  const meta = GATE_META[gate];
  const Icon = meta?.icon || Clock;
  return (
    <div className="flex items-start gap-3 py-2" data-testid={`cert-gate-${gate}`}>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
        ok ? 'bg-emerald-500/15 text-emerald-700' : 'bg-rose-500/10 text-rose-700'
      }`}>
        {ok ? <CheckCircle2 className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
      </div>
      <div className="flex-1">
        <p className="text-sm font-semibold text-[color:var(--tf-forest)] inline-flex items-center gap-2">
          <Icon className="w-3.5 h-3.5" style={{ color: 'var(--tf-gold)' }} />
          {meta?.label || gate}
        </p>
        {extra && <p className="text-xs text-[color:var(--tf-muted)] mt-0.5">{extra}</p>}
      </div>
    </div>
  );
};

const TfvfCertification = () => {
  const { token, user } = useAuth();
  const [mine, setMine] = useState(null);
  const [teamRows, setTeamRows] = useState([]);
  const [teamEval, setTeamEval] = useState({});
  const [issuing, setIssuing] = useState(null);
  const [loading, setLoading] = useState(true);

  const isManager = user && ['master_admin','admin','domain_admin','office_manager','domain_supervisor','supervisor','manager'].includes(user.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const promises = [axios.get(`${API}/tfvf/certification/me`, { headers: authHeaders(token) })];
      if (isManager) promises.push(axios.get(`${API}/tfvf/manager/dashboard`, { headers: authHeaders(token) }));
      const [me, dash] = await Promise.all(promises);
      setMine(me.data);
      if (dash) {
        const rows = dash.data?.rows || [];
        setTeamRows(rows);
        // Fetch evaluation for each team member in parallel.
        const evals = await Promise.all(rows.map(r =>
          axios.get(`${API}/tfvf/certification/user/${r.user_id}`, { headers: authHeaders(token) })
            .then(res => [r.user_id, res.data]).catch(() => [r.user_id, null]),
        ));
        setTeamEval(Object.fromEntries(evals));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load');
    } finally { setLoading(false); }
  }, [token, isManager]);

  useEffect(() => { if (token) load(); }, [token, load]);

  const issue = async (uid) => {
    setIssuing(uid);
    try {
      const r = await axios.post(`${API}/tfvf/certification/user/${uid}/issue`, {}, { headers: authHeaders(token) });
      toast.success(`Certificate issued for ${r.data.user_name}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Issue blocked');
    } finally { setIssuing(null); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[color:var(--tf-forest)]/50" /></div>;

  return (
    <div className="space-y-5" data-testid="tfvf-certification-page">
      {/* Personal certification */}
      {mine && (
        <section className="tf-card rounded-xl p-6" data-testid="tfvf-my-certification">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <p className="tf-eyebrow">My certification status</p>
              <h2 className="text-2xl font-bold text-[color:var(--tf-forest)] mt-1">
                {mine.current_certificate
                  ? <>Certified <ShieldCheck className="w-6 h-6 inline text-emerald-600 ml-1" /></>
                  : mine.eligible_for_certification
                    ? <>Ready for certification <Award className="w-6 h-6 inline text-amber-500 ml-1" /></>
                    : 'In progress'}
              </h2>
              <p className="text-xs text-[color:var(--tf-muted)] mt-1">
                Learning path: {mine.training_role?.replace(/_/g, ' ')} · program v{mine.program_version}
              </p>
            </div>
            {mine.current_certificate && (
              <div className="text-right" data-testid="tfvf-current-cert">
                <p className="tf-eyebrow">Certificate</p>
                <p className="text-xs text-[color:var(--tf-slate)]">Issued {new Date(mine.current_certificate.issued_at).toLocaleDateString()}</p>
                <p className="text-xs text-[color:var(--tf-slate)]">By {mine.current_certificate.issued_by_name}</p>
                <p className="text-[10px] font-mono text-[color:var(--tf-muted)] mt-1">#{mine.current_certificate.id.slice(0, 8)}</p>
              </div>
            )}
          </div>

          {/* Gates */}
          <div className="mt-4 border-t border-[color:var(--tf-forest)]/10 pt-3">
            <GateRow gate="modules_complete" ok={mine.gates.modules_complete} extra={`${mine.modules.completed.length} / ${mine.modules.required.length} required modules · ${mine.modules.pct}%`} />
            <GateRow gate="knowledge_pass"   ok={mine.gates.knowledge_pass}   extra={`Average knowledge score: ${mine.knowledge_score_avg}%`} />
            <GateRow gate="practical_pass"   ok={mine.gates.practical_pass}   extra={mine.best_practical ? `Best: ${mine.best_practical.total_score}/100 · signed off by ${mine.best_practical.manager_name}` : 'No practical assessment yet'} />
            <GateRow gate="acknowledgements" ok={mine.gates.acknowledgements} extra={mine.acknowledgement_signed_at ? `Signed ${new Date(mine.acknowledgement_signed_at).toLocaleDateString()}` : 'Not yet signed'} />
            <GateRow gate="real_call_demo"   ok={mine.gates.real_call_demo}   extra={mine.real_call_demo.pending ? `Pending — needs ${3 - mine.real_call_demo.total_reviewed} more relevant calls attached to your active coaching` : `Demonstrated in ${mine.real_call_demo.demonstrated_in} of ${mine.real_call_demo.total_reviewed} calls`} />
          </div>
        </section>
      )}

      {/* Manager view */}
      {isManager && teamRows.length > 0 && (
        <section className="tf-card rounded-xl p-5">
          <p className="tf-eyebrow mb-3 flex items-center gap-2"><Users className="w-3 h-3" /> Team certification status</p>
          <div className="divide-y divide-[color:var(--tf-forest)]/10">
            {teamRows.map(row => {
              const ev = teamEval[row.user_id];
              if (!ev) return null;
              const passed = Object.values(ev.gates).filter(Boolean).length;
              const total = Object.keys(ev.gates).length;
              return (
                <div key={row.user_id} className="py-3 flex items-center gap-4" data-testid={`cert-user-${row.user_id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[color:var(--tf-forest)]">{row.name || row.email}</p>
                    <p className="text-[11px] text-[color:var(--tf-muted)]">{row.site || 'No site'} · {ev.training_role}</p>
                    <div className="flex gap-1 mt-1">
                      {Object.entries(ev.gates).map(([k, ok]) => (
                        <span key={k} title={GATE_META[k]?.label} className={`w-3 h-3 rounded-full ${ok ? 'bg-emerald-500' : 'bg-rose-400'}`} />
                      ))}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs text-[color:var(--tf-muted)]">{passed}/{total} gates</p>
                    {ev.current_certificate ? (
                      <p className="text-xs text-emerald-700 font-semibold inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Certified</p>
                    ) : ev.eligible_for_certification ? (
                      <button
                        onClick={() => issue(row.user_id)}
                        disabled={issuing === row.user_id}
                        className="tf-btn-gold rounded-lg px-3 py-1 text-xs font-semibold mt-1 inline-flex items-center gap-1 disabled:opacity-50"
                        data-testid={`cert-issue-${row.user_id}`}
                      >
                        {issuing === row.user_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Award className="w-3 h-3" />}
                        Issue certificate
                      </button>
                    ) : (
                      <p className="text-xs text-[color:var(--tf-muted)] inline-flex items-center gap-1 justify-end">Not yet <ChevronRight className="w-3 h-3" /></p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
};

export default TfvfCertification;
